<!-- @TODO - move to design system -->

<script lang="ts">
	import { Text, Icon, CircleAvatar } from '@Shelly/shelly-design-system';
	import { createEventDispatcher } from 'svelte';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import {
		getCameraComponentKey,
		isCameraPrivacyEnabled,
		requestVideoFullscreen,
	} from 'src/components/Camera/helpers/camera-helpers';
	import {
		computeCameraState,
		isCameraFirmwareUpdateRequired,
		isCameraPlaceholderState,
	} from 'src/components/Camera/helpers/camera-state-helpers';
	import { hasActiveWebRTCSession } from 'src/components/Camera/helpers/webrtc-session-manager';
	import { getAvatar } from 'src/components/Dashboards/dash-helpers';
	import { user_data } from 'src/stores';
	import DashCameraWidgetHeader from './components/DashCameraWidgetHeader.svelte';
	import DashCameraOverlayActions from './components/DashCameraOverlayActions.svelte';
	import CameraTile from 'src/components/Camera/MonitoringDashboard/CameraTile.svelte';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { CameraStreamer } from 'src/components/Camera/types/camera-status';
	import type { VideoContainerState } from 'src/components/Camera/types/video-container';
	import type { CircleAvatarPropsTypes } from '@Shelly/shelly-design-system';

	type AdditionalText = {
		value: string;
		icon?: string;
		type?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'muted';
	};

	type OverlaySize = 's' | 'm' | 'l';
	type AvatarSize = Extract<CircleAvatarPropsTypes['size'], 'm' | 'm_plus'>;

	// The colours DashDeviceWidget gives its additional text, so a camera card's
	// status line matches every other device card. The muted utility is named
	// `text-color-muted`, and `default` falls through to the card's own colour.
	const additionalTextColors: Record<NonNullable<AdditionalText['type']>, string> = {
		default: '',
		primary: 'text-primary',
		success: 'text-success',
		warning: 'text-warning',
		danger: 'text-danger',
		muted: 'text-color-muted',
	};

	function getOverlaySize(width: number): OverlaySize {
		if (width >= 500) return 'l';
		if (width >= 300) return 'm';
		return 's';
	}

	/** Same muted treatment the offline card's own message already gets. */
	function blockedAdditionalText(message: string): AdditionalText {
		return { value: message, type: 'muted' };
	}

	let className = '';
	export { className as class };

	export let value: string;
	export let offline = false;
	export let additionalText: AdditionalText | undefined;
	export let isFocusable = false;
	export let selected = false;
	export let hideRightTitleSlot = false;
	export let hideMainSlot = false;

	export let previewImage = '';
	export let imageFit: 'cover' | 'contain' = 'cover';
	export let offlineIcon = 'fa-light fa-cloud-slash';

	const defaultPlaceholderSrc = load_image_from('images/device_images/logo_white_S.png');

	export let arrowRight = false;
	export let arrowRightIcon = 'fa-regular fa-ellipsis-vertical';
	export let active = false;
	export let editMode = false;
	/** Hide the edit overlay slot (matches DashDeviceWidget hideSlotEditModeAction). */
	export let hideSlotEditModeAction = false;

	export let device: Device | undefined;
	export let status: DeviceStatusG2 | undefined;
	export let settings: DeviceSettingsG2 | undefined;
	export let showLiveBadge = false;
	export let headerCompact = false;
	/** Smaller header controls for camera button card grid tiles. */
	export let denseOverlay = false;
	/**
	 * Overlay the name on the media instead of laying it out in the card body
	 * (device widget cards). The avatar then only appears when there is no
	 * picture behind it.
	 */
	export let showNameOnHover = false;
	/** Small device avatar in the top-left corner (device widget cards). */
	export let showDeviceAvatar = false;
	/** Mount live WebRTC player as full-bleed background instead of a snapshot image. */
	export let useLiveFeed = false;
	/** @deprecated Live state is a dot at the top left, the rest of the status stays top right. Kept so older callers still compile. */
	export let showLiveIndicator = false;
	/** Show native-video fullscreen control (dashboard widgets & camera monitoring only). */
	export let showFullscreen = false;

	const dispatch = createEventDispatcher();

	let tileVideoState: VideoContainerState = null;
	let tileIsLive = false;

	$: showStatusHeader = !!device;
	$: isSupportAccess = $user_data?.temporary === true;
	$: cameraKey = device ? getCameraComponentKey(device) : '';
	$: streamer = (status?.[cameraKey] as { streamer?: CameraStreamer } | undefined)?.streamer;
	$: firmwareUpdateRequired = isCameraFirmwareUpdateRequired(device, status);
	$: privacyMode = isCameraPrivacyEnabled(device, status, settings);
	$: streams = (status?.[cameraKey] as { streams?: number } | undefined)?.streams;
	$: cameraBlockState =
		device && status
			? computeCameraState(
					privacyMode,
					!!status.online,
					streamer,
					firmwareUpdateRequired,
					streams,
					hasActiveWebRTCSession(device.id),
					isSupportAccess,
				)
			: null;
	$: overlayState = cameraBlockState ?? tileVideoState;
	$: isPlaceholder = isCameraPlaceholderState(overlayState);
	$: isStreaming =
		!cameraBlockState && overlayState?.type !== 'error' && overlayState?.type !== 'loading';
	// A Support Access session gets no picture at all, not even a stored one.
	$: showPreview = !!previewImage && !isSupportAccess;
	$: useLiveFeedBackground =
		!editMode && useLiveFeed && !!device && !isCameraPlaceholderState(cameraBlockState);
	// Every state but `loading` means there is no live picture: offline, firmware,
	// privacy, support access, stream error and the streamer transitions
	// (starting / stopping / stopped).
	$: blockedState = overlayState && overlayState.type !== 'loading' ? overlayState : null;
	// Only offline keeps its last frame (greyed). Any other blocked state would
	// pass a stale frame off as a live one, so it falls back to the placeholder.
	$: showCameraSnapshot = showPreview && (!blockedState || blockedState.type === 'offline');
	// With no live feed and no snapshot the background paints the S-logo, so a
	// card that has never recorded looks like a blocked one.
	$: showLogoPlaceholder = !useLiveFeedBackground && !showCameraSnapshot;
	$: deviceAvatar = device ? getAvatar(device) : undefined;
	// A card with no picture has nothing but the avatar to identify the device by.
	// One showing a picture carries the name only — the avatar would just cover
	// part of the frame. A blocked player shows a placeholder face, not a frame,
	// so a starting streamer counts as pictureless too.
	$: hasNoPicture = offline || !!blockedState || showLogoPlaceholder;
	$: showAvatar = showDeviceAvatar && !!device && !editMode && (!showNameOnHover || hasNoPicture);
	// A snapshot card holds no player, so it can never be live regardless of how
	// unblocked the camera looks.
	$: headerShowLiveBadge = (showLiveBadge || showLiveIndicator) && useLiveFeedBackground && tileIsLive;
	// Privacy / firmware / support access / stream error / starting say why there
	// is no picture, in the same spot the offline card puts its message. Offline
	// is left alone: its own message already comes down as `additionalText`.
	$: blockedMessage =
		blockedState && blockedState.type !== 'offline' ? (blockedState.heading ?? '') : '';
	$: cardAdditionalText = blockedMessage ? blockedAdditionalText(blockedMessage) : additionalText;
	$: showInFlowChrome = !showNameOnHover || editMode;
	$: showAdditionalText =
		showInFlowChrome && !!cardAdditionalText?.value && (!$$slots.main || hideMainSlot);
	$: showOverlayChrome = showNameOnHover && !!value && !editMode;
	$: showEditModeAction = editMode && !hideSlotEditModeAction && $$slots.editModeAction;
	// Live feed, snapshot and logo placeholder all sit under the same gradient,
	// which is near-black where the text goes, so text/icons are always white.
	$: additionalTextClass = additionalTextColors[cardAdditionalText?.type ?? 'default'] || 'text-white';
	$: hasSupplementaryInfo = showAdditionalText || (!!$$slots.main && !hideMainSlot);

	let rootEl: HTMLDivElement;
	let cardWidth = 0;

	let overlaySize: OverlaySize = 's';
	let avatarSize: AvatarSize = 'm';
	$: overlaySize = getOverlaySize(cardWidth);
	$: iconOnly = overlaySize === 's';
	$: avatarSize = overlaySize === 'l' ? 'm_plus' : 'm';

	function handleKeydown(e: KeyboardEvent) {
		if (!isFocusable) return;
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			rootEl?.click();
		}
	}

	function handleArrowClick(e: CustomEvent<MouseEvent>) {
		e.detail?.stopPropagation();
		dispatch('arrow-click', e.detail ?? e);
	}

	function handleFullscreenClick(e: CustomEvent<MouseEvent>) {
		e.detail?.stopPropagation();
		const videoElement = rootEl?.querySelector('video') as HTMLVideoElement | null;
		if (requestVideoFullscreen(videoElement)) return;
		dispatch('fullscreen', e.detail ?? e);
	}

	$: showTopMenu = arrowRight && !editMode && showStatusHeader;
	$: showFullscreenButton = showFullscreen && !editMode && isStreaming && showStatusHeader;

	function handleImageError() {
		previewImage = '';
	}
</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
	bind:this={rootEl}
	bind:clientWidth={cardWidth}
	class="dash-camera-device-card {className}"
	class:is-focusable={isFocusable && !editMode}
	class:offline
	class:is-placeholder={isPlaceholder}
	class:selected
	class:active={active && !offline && !editMode}
	class:dense-overlay={denseOverlay}
	class:show-name-on-hover={showNameOnHover}
	class:has-corner-actions={showTopMenu || showFullscreenButton}
	class:edit-mode={editMode}
	class:size-s={overlaySize === 's'}
	class:size-m={overlaySize === 'm'}
	class:size-l={overlaySize === 'l'}
	role={isFocusable ? 'button' : 'group'}
	tabindex={isFocusable && !editMode ? 0 : -1}
	{...$$restProps}
	on:click
	on:keydown={handleKeydown}
	on:drag
	on:dragenter
	on:dragleave
	on:dragover
	on:dragstart
	on:drop
>
	<div class="background">
		{#if useLiveFeedBackground && device}
			<div class="live-background">
				<CameraTile
					bind:videoState={tileVideoState}
					bind:isLive={tileIsLive}
					camera={device}
					editMode={false}
					mediaFit={imageFit}
					embedMode
					backgroundOnly
					fillContainer
				/>
			</div>
			<div class="gradient-overlay"></div>
		{:else if showCameraSnapshot}
			<img
				class="preview"
				src={previewImage}
				alt={value}
				style={`object-fit: ${imageFit};`}
				on:error={handleImageError}
				draggable="false"
			/>
			<div class="gradient-overlay"></div>
		{:else}
			<img class="preview-placeholder" src={defaultPlaceholderSrc} alt="" draggable="false" />
			<div class="gradient-overlay"></div>
		{/if}
	</div>

	{#if showAvatar && deviceAvatar}
		<div class="device-avatar" aria-hidden="true">
			<CircleAvatar
				size={avatarSize}
				disabled={offline}
				image={deviceAvatar.image}
				icon={deviceAvatar.icon}
				defaultImageIcon="fak fa-solid-shelly"
				backgroundColor={deviceAvatar.backgroundColor || undefined}
				noBackground={false}
			/>
		</div>
	{/if}

	{#if offline && !editMode && !showStatusHeader}
		<div class="offline-indicator">
			<Icon icon={offlineIcon} size="s" class={showPreview ? 'text-white' : 'text-color-muted'} />
		</div>
	{/if}

	{#if showStatusHeader && !editMode}
		<div class="status-header">
			<DashCameraWidgetHeader
				{device}
				{status}
				{settings}
				showLiveBadge={headerShowLiveBadge}
				compact={headerCompact}
				{denseOverlay}
				{iconOnly}
				{overlaySize}
				{overlayState}
			/>
		</div>
	{/if}

	{#if (showTopMenu || showFullscreenButton) && !editMode}
		<div class="corner-actions">
			<DashCameraOverlayActions
				showMenu={showTopMenu}
				showFullscreen={showFullscreenButton}
				dense={denseOverlay || overlaySize === 's'}
				menuIcon={arrowRightIcon}
				on:menu-click={handleArrowClick}
				on:fullscreen={handleFullscreenClick}
			/>
		</div>
	{/if}

	{#if showOverlayChrome}
		<div class="overlay-chrome">
			{#if cardAdditionalText?.value}
				<Text styleType="body-extra-small" class={additionalTextClass} noWrap>
					{cardAdditionalText.value}
				</Text>
			{/if}
			<Text styleType="body-small" class="text-500 text-white" noWrap>{value}</Text>
		</div>
	{/if}

	{#if showEditModeAction}
		<div class="edit-mode-action">
			<slot name="editModeAction" />
		</div>
	{/if}

	<div class="content">
		<div class="bottom-row">
			<div class="value-section-wrapper">
				{#if showAdditionalText}
					<div
						class="d-flex gap-xs align-items-center additional-text-row {additionalTextClass}"
					>
						{#if cardAdditionalText?.icon}
							<Icon size="xs" icon={cardAdditionalText.icon} />
						{/if}
						<Text styleType="body-extra-small" noWrap>{cardAdditionalText.value}</Text>
					</div>
				{/if}

				{#if $$slots.main && !hideMainSlot && !editMode}
					<div class="main-slot">
						<slot name="main" />
					</div>
				{/if}

				{#if showInFlowChrome}
					<div class="value-section">
						<Text
							noWrap
							class="flex-1 min-width-0 text-500 text-white {hasSupplementaryInfo
								? ''
								: 'ellipse-2-lines'}"
							styleType="body-small"
						>
							{value}
						</Text>
						{#if $$slots.rightTitle && !hideRightTitleSlot && !editMode}
							<div class="right-title-slot">
								<slot name="rightTitle" />
							</div>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</div>
</div>

<style>
	.dash-camera-device-card {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		min-height: 114px;
		border-radius: var(--radius-big);
		overflow: hidden;
		background-color: var(--color-1);
		outline: none;
		--camera-overlay-inset: var(--spacing-s);
		--camera-avatar-inset: var(--spacing-s);
		--camera-name-inset: var(--spacing-s-plus);
	}

	.size-s {
		--camera-overlay-inset: var(--spacing-xs);
	}

	.size-m {
		--camera-avatar-inset: var(--spacing-s-plus);
	}

	.size-l {
		--camera-overlay-inset: var(--spacing-s-plus);
		--camera-avatar-inset: var(--spacing-m);
	}

	.dense-overlay.size-s {
		--camera-overlay-inset: var(--spacing-xxs);
	}

	.offline {
		background-color: var(--color-0);
	}

	.active {
		background-color: var(--color-white);
	}

	.background,
	.live-background {
		position: absolute;
		inset: 0;
	}

	.background {
		z-index: 0;
		pointer-events: none;
	}

	.live-background {
		overflow: hidden;
	}

	.preview,
	.preview-placeholder,
	.live-background :global(.state-placeholder) {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	/* Stays in flow: ZoomableContainer's transformed .zoom-content is the
	   containing block for absolute children, and it has no height of its own. */
	.live-background :global(.video-player-area video) {
		width: 100%;
		height: 100%;
		aspect-ratio: unset;
		object-fit: cover;
		object-position: center;
	}

	.preview,
	.preview-placeholder {
		background-color: var(--color-0);
	}

	.preview-placeholder {
		object-fit: contain;
		padding: var(--spacing-xl);
		opacity: 0.35;
		background-color: var(--color-1);
	}

	.offline .preview {
		filter: grayscale(100%);
		opacity: 0.4;
	}

	.live-background :global(.camera-tile-root),
	.live-background :global(.camera-tile-widget) {
		width: 100%;
		height: 100%;
	}

	.live-background :global(.camera-tile-widget) {
		aspect-ratio: unset;
	}

	.live-background :global(.video-container-wrapper),
	.live-background :global(.video-player-area),
	.live-background :global(.zoom-container),
	.live-background :global(.zoom-content) {
		height: 100%;
	}

	.device-avatar {
		position: absolute;
		top: var(--camera-avatar-inset);
		left: var(--camera-avatar-inset);
		z-index: 4;
		pointer-events: none;
		line-height: 0;
	}

	/* Bounded on both sides so a long name ellipsises instead of running under
	   the corner actions. */
	.overlay-chrome {
		position: absolute;
		left: var(--camera-name-inset);
		right: var(--camera-name-inset);
		bottom: var(--camera-name-inset);
		z-index: 3;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		pointer-events: none;
	}

	.has-corner-actions .overlay-chrome {
		right: calc(var(--camera-name-inset) + var(--spacing-xl));
	}

	.offline .gradient-overlay,
	.is-placeholder .gradient-overlay {
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.55) 0%,
			rgba(0, 0, 0, 0.35) 40%,
			rgba(0, 0, 0, 0.75) 70%,
			rgba(0, 0, 0, 0.92) 100%
		);
	}

	.edit-mode-action,
	.offline-indicator {
		position: absolute;
		top: var(--camera-overlay-inset);
		right: var(--camera-overlay-inset);
	}

	.edit-mode-action {
		z-index: 5;
		pointer-events: auto;
	}

	.offline-indicator {
		z-index: 2;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.status-header {
		position: absolute;
		top: 0;
		right: 0;
		left: 0;
		z-index: 3;
		display: flex;
		justify-content: flex-end;
		align-items: flex-start;
		gap: var(--spacing-xs);
		padding: var(--camera-overlay-inset);
		pointer-events: none;
	}

	.corner-actions {
		position: absolute;
		right: var(--camera-overlay-inset);
		bottom: var(--camera-overlay-inset);
		z-index: 3;
	}

	.gradient-overlay {
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.35) 0%,
			rgba(0, 0, 0, 0.05) 28%,
			rgba(0, 0, 0, 0.05) 40%,
			rgba(0, 0, 0, 0.55) 64%,
			rgba(0, 0, 0, 0.9) 100%
		);
		pointer-events: none;
	}

	.content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		flex: 1;
		min-height: 0;
		padding: var(--camera-name-inset);
		gap: var(--spacing-xs);
	}

	.has-corner-actions .value-section {
		padding-right: var(--spacing-xl);
	}

	.bottom-row {
		display: flex;
		align-items: flex-end;
		margin-top: auto;
	}

	.value-section-wrapper,
	.value-section {
		display: flex;
		flex: 1;
		min-width: 0;
	}

	.value-section-wrapper {
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.value-section {
		align-items: flex-end;
		gap: var(--spacing-s);
	}

	.value-section :global(.ellipse-2-lines) {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.right-title-slot,
	.main-slot,
	.additional-text-row {
		min-width: 0;
	}

	.is-focusable {
		cursor: pointer;
	}

	.is-focusable:focus-visible:not(.selected) {
		box-shadow: 0 0 0 4px rgba(36, 145, 255, 0.3);
	}

	.is-focusable:focus-visible:not(.selected)::after,
	.selected::after {
		content: '';
		position: absolute;
		inset: 0;
		border: 2px solid var(--color-primary);
		border-radius: inherit;
		pointer-events: none;
		z-index: 3;
	}
</style>
